// White-label registry for the Voluntary Alignment form.
//
// Two factors drive the templates.
//
//   Brand  The ACO that runs the programme. The brand owns the logo, the
//          legal name, the ACO website, and the ACO phone numbers.
//   Group  The provider group that the beneficiary attends. The group owns
//          the provider group name, the TIN, the return address, and the fax.
//
// One brand holds many groups. The page picks the group from the DNS name
// first, and from the URL path second. The service receives only the group
// key, and it reads the brand from its own registry.
//
// To add a group:
//   1. Add a record to GROUPS. Use the same key as the URL path.
//   2. Set `brandKey` to the owning brand.
//   3. List the DNS names the group uses in `hostnames`. A bare domain also
//      matches all of its subdomains. An empty list means the URL path only.
//   4. Add the same key to `WhiteLabel:Groups` in the service appsettings.json.
//
// To add a brand:
//   1. Add a record to BRANDS.
//   2. Copy the logo into `public/` and point `logo` at it.
//   3. Add the same key to `WhiteLabel:Brands` in appsettings.json.

export const DEFAULT_BRAND_KEY = "ccmd";
export const DEFAULT_GROUP_KEY = "bridgecare-provider-group-ma";

export const BRANDS = {
  ccmd: {
    key: "ccmd",
    documentTitle: "SVA Form",
    logo: "./CCMD_DCE.png",
    logoAlt: "CareConnectMD DCE logo",
    // Legal name used in the English letter.
    legalName: "CareConnectMD DCE LLC",
    // Short name used in the Spanish letter and the page footer.
    shortName: "CareConnectMD",
    phone: "888-874-0818",
    phoneWithCountryCode: "1-888-874-0818",
    tty: "711",
    website: "https://careconnectmd.com",
    acoWebsite: "https://www.careconnectmd.com/aco-reach",
  },
};

export const GROUPS = {
  "bridgecare-provider-group-ma": {
    key: "bridgecare-provider-group-ma",
    brandKey: "ccmd",
    hostnames: [],
    providerGroupName: "Bridgecare Provider Group MA, PLLC",
    baseProviderTin: "334874406",
    returnName: "CareConnectMD DCE LLC",
    address: {
      line1: "3090 Bristol St Ste 200",
      line2: "Costa Mesa, CA 92626",
    },
    fax: "877-738-0868",
  },
  "trilogy-np": {
    key: "trilogy-np",
    brandKey: "ccmd",
    hostnames: [],
    providerGroupName: "Trilogy NP, LLC",
    baseProviderTin: "395069160",
    returnName: "CareConnectMD DCE LLC",
    address: {
      line1: "3090 Bristol St Ste 200",
      line2: "Costa Mesa, CA 92626",
    },
    fax: "877-738-0868",
  },
};

// Strips the port, the trailing dot, and the letter case from a hostname.
const normalizeHostname = (hostname) =>
  String(hostname || "")
    .trim()
    .toLowerCase()
    .split(":")[0]
    .replace(/\.$/, "");

/**
 * Returns the group key that owns a hostname, or null.
 *
 * An exact entry in `hostnames` wins. After that the longest matching parent
 * domain wins, so `sva.west.example.com` prefers `west.example.com` over
 * `example.com`. An unknown hostname returns null.
 */
export const resolveGroupKeyByHostname = (hostname, groups = GROUPS) => {
  const host = normalizeHostname(hostname);
  if (!host) return null;

  const entries = Object.entries(groups);

  for (const [key, group] of entries) {
    const names = group.hostnames || [];
    if (names.some((name) => normalizeHostname(name) === host)) return key;
  }

  let bestKey = null;
  let bestLength = 0;

  for (const [key, group] of entries) {
    for (const name of group.hostnames || []) {
      const domain = normalizeHostname(name);
      if (!domain || !host.endsWith(`.${domain}`)) continue;
      if (domain.length > bestLength) {
        bestKey = key;
        bestLength = domain.length;
      }
    }
  }

  return bestKey;
};

/**
 * Returns the brand and the group for a hostname and a URL path key.
 *
 * The DNS name wins. The URL path comes second. An unknown path returns a
 * null group, so the page can show the invalid group message.
 */
export const resolveSite = (hostname, pathKey, brands = BRANDS, groups = GROUPS) => {
  const hostKey = resolveGroupKeyByHostname(hostname, groups);
  const key = hostKey || pathKey;
  const group = key ? groups[key] : undefined;

  if (!group) return { brand: null, group: null };

  const brand = brands[group.brandKey] || brands[DEFAULT_BRAND_KEY];
  if (!brand) return { brand: null, group: null };

  return { brand, group };
};
